// ============================================================================
// src/lib/ruminantOptionOperationApi.ts — client of public.ruminant_option_operation
// ----------------------------------------------------------------------------
// Migration 248 (R-I: cattle_breeds, cattle_origins, sheep_breeds, sheep_origins)
// moves the five dropdown option pre-seed / inline-create writes onto ONE
// audited SECURITY DEFINER entrypoint with private immutable request receipts
// in public.ruminant_option_operation_receipts:
//
//   * `cattle.breed.ensure`  -> public.cattle_breeds
//   * `cattle.origin.ensure` -> public.cattle_origins
//   * `sheep.breed.ensure`   -> public.sheep_breeds
//   * `sheep.origin.ensure`  -> public.sheep_origins
//
// Each operation is idempotent under a transaction-scoped per-(table, id)
// advisory lock (C3):
//   * If the option id does not exist yet, it inserts `(id, label, active)`,
//     records a receipt, and returns `{ok: true, kind: 'saved', written: true, option, ...}`.
//   * If the option id already exists, it preserves the stored label/active
//     without overwriting and returns `{ok: true, kind: 'no_change', written: false, option, ...}`.
//
// Transport posture matches src/lib/appStoreOperationApi.ts and
// src/lib/cattleOperationsApi.ts:
//   * One requestId per logical action; transport timeouts/network errors retry
//     up to 3 times (8s race, 1s/2s backoff) with the SAME requestId so the
//     server receipt deduplicates.
//   * A database SQLSTATE refusal (22023, 42501, WCF01, ...) is an authoritative
//     answer and returns immediately without retry.
// ============================================================================

import {newClientSubmissionId} from './clientSubmissionId.js';
import type {SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';

export type RuminantOptionOperation =
  | 'cattle.breed.ensure'
  | 'cattle.origin.ensure'
  | 'sheep.breed.ensure'
  | 'sheep.origin.ensure';

export type RuminantOptionRow = {
  id: string;
  label: string;
  active: boolean;
  created_at?: string | null;
};

export type RuminantOptionOpRequest = {
  requestId: string;
  operation: RuminantOptionOperation;
  target?: Record<string, never>;
  expected?: Record<string, never>;
  change: Record<string, unknown>;
};

export type RuminantOptionOpOutcome =
  | {
      status: 'saved';
      written: true;
      option: RuminantOptionRow | null;
      replayed: boolean;
    }
  | {
      status: 'no_change';
      written: false;
      option: RuminantOptionRow | null;
    }
  | {
      status: 'rejected';
      code: string;
      message: string;
      details?: string;
      hint?: string;
    }
  | {
      status: 'unknown';
      message: string;
      mustReread: true;
    };

const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
const SQLSTATE = /^[0-9A-Z]{5}$/;

type RpcError = {code?: string; message?: string; details?: string; hint?: string} | null;

/** A fresh request id for one logical ruminant option operation. */
export function newRuminantOptionRequestId(prefix = 'ruminant-opt'): string {
  const cleanPrefix = prefix.replace(/-+$/, '');
  return `${cleanPrefix}-${newClientSubmissionId()}`;
}

function parseOptionRow(raw: unknown): RuminantOptionRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.label !== 'string' || typeof r.active !== 'boolean') {
    return null;
  }
  return {
    id: r.id,
    label: r.label,
    active: r.active,
    created_at: typeof r.created_at === 'string' ? r.created_at : null,
  };
}

/** Interpret one reply from public.ruminant_option_operation. */
export function interpretRuminantOptionReply(request: RuminantOptionOpRequest, data: unknown): RuminantOptionOpOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  if (d.request_id !== request.requestId || d.operation !== request.operation) {
    return {status: 'unknown', message: 'Could not confirm this option was saved. Reload to check.', mustReread: true};
  }
  switch (d.kind) {
    case 'saved': {
      if (d.ok !== true) break;
      return {
        status: 'saved',
        written: true,
        option: parseOptionRow(d.option),
        replayed: d.replayed === true,
      };
    }
    case 'no_change': {
      if (d.ok !== true) break;
      return {
        status: 'no_change',
        written: false,
        option: parseOptionRow(d.option),
      };
    }
  }
  return {status: 'unknown', message: 'Could not confirm this option was saved. Reload to check.', mustReread: true};
}

/**
 * Execute one `public.ruminant_option_operation` RPC call.
 * Never throws on PostgREST/transport errors. Retries transport timeouts/errors
 * with the SAME requestId; returns `rejected` immediately on a 5-character SQLSTATE.
 */
export async function applyRuminantOptionOperation(
  sb: SupabaseClient,
  request: RuminantOptionOpRequest,
): Promise<RuminantOptionOpOutcome> {
  if (!sb) throw new Error('applyRuminantOptionOperation: sb required');
  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const call = sb.rpc('ruminant_option_operation', {
        p_request_id: request.requestId,
        p_operation: request.operation,
        p_target: request.target ?? {},
        p_expected: request.expected ?? {},
        p_change: request.change,
      });
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call, timeout])) as {data: unknown; error: RpcError};
      if (error) {
        if (error.code && SQLSTATE.test(error.code)) {
          return {
            status: 'rejected',
            code: error.code,
            message: error.message || 'The option was refused.',
            ...(error.details !== undefined ? {details: error.details} : {}),
            ...(error.hint !== undefined ? {hint: error.hint} : {}),
          };
        }
        throw new Error(error.message || 'transport');
      }
      return interpretRuminantOptionReply(request, data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  console.warn('ruminant_option_operation: no confirmed reply after retries:', lastMessage);
  return {status: 'unknown', message: 'Could not reach the server. Your change is kept; retry.', mustReread: true};
}

/** Map a SQLSTATE rejection to a user-facing message. */
export function ruminantOptionRejectionMessage(
  outcome: Extract<RuminantOptionOpOutcome, {status: 'rejected'}>,
): string {
  switch (outcome.code) {
    case '22023':
      return outcome.message.replace(/^ruminant_option_operation:\s*/, '');
    case '42501':
      return 'Your session or role changed. Sign in again; your change is kept.';
    case 'WCF01':
      return 'This request id belongs to different saved work. Reload and try again.';
    default:
      return outcome.message || 'The option was refused.';
  }
}

/**
 * Map one `RuminantOptionOpOutcome` onto the non-throwing `{data, error}`
 * envelope required by `cattleImportApi.ts` and `sheepImportApi.ts`.
 * Both `'saved'` and `'no_change'` resolve with `{data, error: null}`.
 */
export function ruminantOptionOutcomeToResult(outcome: RuminantOptionOpOutcome): {
  data: unknown;
  error: (SupabaseErrorLike & {code?: string}) | null;
} {
  switch (outcome.status) {
    case 'saved':
    case 'no_change':
      return {data: outcome.option ?? outcome, error: null};
    case 'rejected':
      return {data: null, error: {code: outcome.code, message: ruminantOptionRejectionMessage(outcome)}};
    case 'unknown':
      return {data: null, error: {message: outcome.message}};
  }
}

/**
 * Throwing entry-point helper for `routeMutationCatalog.ts` (`cattle.origin.create`).
 * Resolves on `'saved'` or `'no_change'`; throws an `Error` (with `.code`,
 * `.details`, and `.hint` attached when `rejected`) on `'rejected'` or `'unknown'`.
 */
export async function runRuminantOptionOperation(
  sb: SupabaseClient,
  request: RuminantOptionOpRequest,
): Promise<Extract<RuminantOptionOpOutcome, {status: 'saved' | 'no_change'}>> {
  const outcome = await applyRuminantOptionOperation(sb, request);
  if (outcome.status === 'saved' || outcome.status === 'no_change') {
    return outcome;
  }
  if (outcome.status === 'rejected') {
    const err = new Error(ruminantOptionRejectionMessage(outcome)) as Error & {
      code?: string;
      details?: string;
      hint?: string;
    };
    err.code = outcome.code;
    if (outcome.details !== undefined) err.details = outcome.details;
    if (outcome.hint !== undefined) err.hint = outcome.hint;
    throw err;
  }
  throw new Error(outcome.message);
}
